import { z } from 'zod';

/**
 * Handle Data Types
 * All valid data types for node handles
 */
export const HandleDataTypeSchema = z.enum([
  'text', 'image', 'video', 'audio', 'mask',
  'file', '3d', 'code', 'number', 'boolean', 'lora', 'any'
]);

/**
 * Handle Position Side
 */
export const HandlePositionSideSchema = z.enum(['left', 'right', 'top', 'bottom']);

/**
 * Handle Position
 * Can be a simple string ('left', 'right', 'top', 'bottom')
 * or an object with side, offsetY, offsetX, ordered
 */
export const HandlePositionSchema = z.union([
  HandlePositionSideSchema,
  z.object({
    side: HandlePositionSideSchema,
    offsetY: z.number().min(0).max(1).optional(),
    offsetX: z.number().min(0).max(1).optional(),
    ordered: z.number().int().min(0).optional(),
  }),
]);

/**
 * Handle Mode
 */
export const HandleModeSchema = z.enum(['SMALL', 'DEFAULT']).optional();

/**
 * Handle Metadata
 * Parameter metadata stored on handles for reverse lookup
 */
export const HandleMetadataSchema = z.object({
  parameterName: z.string().optional(),
  parameterLabel: z.string().optional(),
  icon: z.string().optional(),
}).passthrough(); // Allow additional metadata fields

/**
 * Handle Meta Schema
 * Complete handle definition for node connections
 */
export const HandleMetaSchema = z.object({
  id: z.string(),
  type: z.enum(['input', 'output']),
  dataType: HandleDataTypeSchema.optional(),
  label: z.string().optional(),
  description: z.string().optional(),
  position: HandlePositionSchema.optional(),
  locked: z.boolean().optional(),
  maxConnections: z.number().int().positive().optional(),
  mode: HandleModeSchema,
  metadata: HandleMetadataSchema.optional(),
});

// TypeScript types
export type HandleDataType = z.infer<typeof HandleDataTypeSchema>;
export type HandlePositionSide = z.infer<typeof HandlePositionSideSchema>;
export type HandlePosition = z.infer<typeof HandlePositionSchema>;
export type HandleMode = z.infer<typeof HandleModeSchema>;
export type HandleMetadata = z.infer<typeof HandleMetadataSchema>;
export type HandleMeta = z.infer<typeof HandleMetaSchema>;
